/**
 * hooks/useTickStream.ts — live tick WebSocket against the router's /ws
 * Durable Object (TICK_STREAM). Replaces the 30-60s polling from
 * useQuery for the focus symbol + watchlist.
 *
 * Connection lifecycle:
 *   - Open on mount, exponential backoff on close (1s → 2s → 4s ... → 30s)
 *   - Re-subscribes whenever the symbol set changes
 *   - Sends 'ping' every 15s; tracks pong RTT for the status indicator
 *   - On 'tick' frames, writes to store.setQuote() so all consumers
 *     (Tape, Watchlist, ChartHeader, Movers) update without re-render
 *     thrash. React Query stays in place as the historical-bars cache;
 *     the WS only updates quotes.
 *
 * Protocol (matches ws_server.py's emit format):
 *   client → { type: 'subscribe', symbols: ['AAPL', 'NVDA'] }
 *   client → { type: 'unsubscribe', symbols: [...] }
 *   client → { type: 'ping' }
 *   server → { type: 'tick', payload: { symbol, bid, ask, last, ts, change_pct } }
 *   server → { type: 'pong' }
 *   server → { type: 'status', payload: { connected: boolean } }
 */
import { useEffect, useRef, useState } from 'react';
import { useStore } from '@/store';
import { api } from '@/lib/api';
import { mintWsTicket, wsUrlWithTicket, WS_CLOSE_SESSION_EXPIRED } from '@/lib/wsTicket';

const WS_URL = (import.meta.env.VITE_WS_URL as string | undefined)
  ?? deriveWsUrl(import.meta.env.VITE_API_BASE);

function deriveWsUrl(apiBase: string | undefined): string {
  // Default: same host as the API, swap https->wss, append /ws.
  if (!apiBase) return 'wss://api.omnistrat.ai/ws';
  return apiBase.replace(/^http/, 'ws').replace(/\/?$/, '') + '/ws';
}

const BACKOFF = [1000, 2000, 4000, 8000, 16000, 30000];
const PING_INTERVAL_MS = 15000;
// No pong within this window of a ping => the socket is half-open (TCP alive,
// feed dead). Force a reconnect so the LIVE pip can't stay green over a frozen
// feed -- a failure rendering as a fact. Must exceed worst-case RTT + server
// ping cadence; 10s is generous.
const PONG_TIMEOUT_MS = 10000;

// One single-use ticket per connect (lib/wsTicket.ts). NO token fallback: the
// router refuses ?token= (401 TOKEN_IN_URL_REFUSED), and a session JWT in a
// URL is a leak into history and edge logs.
const postTicket = () => api<unknown>('/ws/ticket', { method: 'POST', body: JSON.stringify({}) });

export interface TickStreamStatus {
  connected: boolean;
  latencyMs: number | null;
  /** Why the feed is not connected, when we know (e.g. session expired). */
  error?:    string | null;
}

export function useTickStream(): TickStreamStatus {
  const focus     = useStore(s => s.focusSymbol);
  const watchlist = useStore(s => s.watchlist);
  const setQuote  = useStore(s => s.setQuote);

  const [status, setStatus] = useState<TickStreamStatus>({ connected: false, latencyMs: null });

  const wsRef          = useRef<WebSocket | null>(null);
  const mountedRef     = useRef(true);
  const attemptRef     = useRef(0);
  const reconnectRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const pongTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingSentRef    = useRef<number>(0);
  const symbolsRef     = useRef<string[]>([]);
  // Always holds the CURRENT desired subscription set, so a reconnect (whose
  // connect() closure captured the mount-time set) subscribes to the symbol the
  // user is looking at NOW, not the one they had focused when the tab loaded.
  const subscribeSetRef = useRef<string[]>([]);

  // Build the union of (focus + watchlist), dedup, uppercase.
  const subscribeSet = Array.from(new Set([focus, ...watchlist].map(s => s.toUpperCase())));

  // ── (Re)subscribe whenever the symbol set changes and the socket is open.
  useEffect(() => {
    // Record the latest desired set BEFORE the open-check, so it stays current
    // even while disconnected -- the reconnect's onopen reads this ref.
    subscribeSetRef.current = subscribeSet;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    const prev = symbolsRef.current;
    const next = subscribeSet;
    const toAdd    = next.filter(s => !prev.includes(s));
    const toRemove = prev.filter(s => !next.includes(s));
    if (toAdd.length)    ws.send(JSON.stringify({ type: 'subscribe',   symbols: toAdd }));
    if (toRemove.length) ws.send(JSON.stringify({ type: 'unsubscribe', symbols: toRemove }));
    symbolsRef.current = next;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeSet.join(',')]);

  // ── Main connection effect.
  useEffect(() => {
    mountedRef.current = true;

    async function connect() {
      if (!mountedRef.current) return;
      // Prefer a SHORT-LIVED, view-scoped ticket so the long-lived Passport JWT
      // never rides in the wss:// URL (edge logs, devtools, Referer). Fall back
      // to the raw token on ANY failure -- a missing endpoint, a network blip,
      // no signing key -- so the live feed can never blank and deploy order
      // (router vs terminal) does not matter.
      // A FRESH single-use ticket for EVERY connect and reconnect: tickets are
      // consumed at the handshake (WS_TICKET_USED on reuse) and expire in 60s,
      // so a cached one would fail exactly when the feed needs to come back.
      if (!useStore.getState().token) {
        setStatus({ connected: false, latencyMs: null, error: 'Not signed in -- live feed off.' });
        return;
      }
      const t = await mintWsTicket(postTicket);
      if (!mountedRef.current) return;   // may have unmounted during the await
      if (!t.ok) {
        setStatus({ connected: false, latencyMs: null, error: t.reason });
        // Fatal (401/403): retrying cannot fix it. Otherwise back off and
        // mint again.
        if (t.fatal) return;
        const delay = BACKOFF[Math.min(attemptRef.current, BACKOFF.length - 1)];
        attemptRef.current++;
        reconnectRef.current = setTimeout(connect, delay);
        return;
      }
      const url = wsUrlWithTicket(WS_URL, t.ticket);
      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (!mountedRef.current) return;
          attemptRef.current = 0;
          setStatus(s => ({ ...s, connected: true, error: null }));
          // Subscribe to the CURRENT set (ref), not this closure's mount-time
          // capture -- otherwise a reconnect resubscribes stale symbols and the
          // focused ticker goes quiet after any drop.
          const set = subscribeSetRef.current.length ? subscribeSetRef.current : subscribeSet;
          ws.send(JSON.stringify({ type: 'subscribe', symbols: set }));
          symbolsRef.current = set;
          // Ping every 15s; arm a pong watchdog after each ping.
          pingTimerRef.current = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              pingSentRef.current = performance.now();
              ws.send(JSON.stringify({ type: 'ping' }));
              // If this ping isn't answered within PONG_TIMEOUT_MS the socket is
              // dead-but-open: drop to CONNECTING and close so the backoff
              // reconnect runs. Without this, onclose never fires on a silent
              // half-open and the pip stays green over a frozen feed.
              if (pongTimeoutRef.current) clearTimeout(pongTimeoutRef.current);
              pongTimeoutRef.current = setTimeout(() => {
                setStatus({ connected: false, latencyMs: null });
                try { ws.close(); } catch { /* onclose schedules the reconnect */ }
              }, PONG_TIMEOUT_MS);
            }
          }, PING_INTERVAL_MS);
        };

        ws.onmessage = (ev) => {
          if (!mountedRef.current) return;
          let msg: { type: string; payload?: unknown };
          try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : ''); }
          catch { return; }
          if (msg.type === 'tick' && msg.payload && typeof msg.payload === 'object') {
            // MIRRORS TickStreamDO's broadcast (router/src/do/tickStreamDO.ts),
            // which emits `price` and `timestamp`. This type declared only
            // `last` and `ts`, so the compiler accepted reads that were
            // always undefined at runtime.
            const p = msg.payload as {
              symbol: string; bid?: number; ask?: number;
              price?: number; timestamp?: number;
              last?: number;
              change?: number; change_pct?: number; ts?: number;
            };
            // A MISSING QUOTE FIELD IS NOT ZERO -- and this is the SOURCE every
            // price in the app flows from. `?? 0` published bid=0/ask=0, which
            // made mid=0 and (via the `last` fallback below) a LIVE QUOTE OF
            // $0.00 for a real symbol; `change_pct ?? 0` was the root of the
            // green "+0.00%" that appeared in four different components.
            // NaN propagates honestly: every consumer already gates on
            // Number.isFinite and renders "--".
            const num = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : NaN);
            const bid = num(p.bid);
            const ask = num(p.ask);
            const mid = Number.isFinite(bid) && Number.isFinite(ask) ? (bid + ask) / 2 : NaN;
            setQuote({
              symbol:     String(p.symbol).toUpperCase(),
              bid, ask,
              mid,
              // TickStreamDO emits `price` and `timestamp`, not `last`
              // and `ts`. Both reads missed, so `last` silently fell back
              // to the mid and every quote was stamped with Date.now() --
              // which defeats staleness checks entirely: a frozen feed
              // looked perfectly fresh because the CLIENT was writing the
              // timestamp. Accept both spellings, prefer the real one.
              last:       num(p.price ?? p.last ?? mid),
              change:     num(p.change),
              change_pct: num(p.change_pct),
              timestamp:  Number(p.timestamp ?? p.ts ?? Date.now()),
            });
          } else if (msg.type === 'pong') {
            // Feed answered -> alive. Clear the watchdog and (re)assert connected
            // in case a prior slow cycle had dropped the pip.
            if (pongTimeoutRef.current) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; }
            const rtt = performance.now() - pingSentRef.current;
            setStatus(s => ({ ...s, connected: true, latencyMs: Math.round(rtt) }));
          } else if (msg.type === 'status' && msg.payload && typeof msg.payload === 'object') {
            const p = msg.payload as { connected?: boolean };
            setStatus(s => ({ ...s, connected: !!p.connected }));
          }
        };

        ws.onclose = (ev) => {
          if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
          if (pongTimeoutRef.current) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; }
          // Null the latency too: a stale "12ms" next to "CONNECTING" reads as
          // a live measurement of a dead socket.
          setStatus(s => ({ ...s, connected: false, latencyMs: null }));
          if (!mountedRef.current) return;
          if (ev.code === 1000) return;  // normal close
          // 4001: the router closed the socket because the SESSION behind its
          // ticket expired. Reconnect promptly with a fresh ticket; if the
          // session is really gone, /ws/ticket answers 401 and connect() stops
          // with "session expired" instead of looping.
          if (ev.code === WS_CLOSE_SESSION_EXPIRED) attemptRef.current = 0;
          const delay = BACKOFF[Math.min(attemptRef.current, BACKOFF.length - 1)];
          attemptRef.current++;
          reconnectRef.current = setTimeout(connect, delay);
        };

        ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
      } catch {
        const delay = BACKOFF[Math.min(attemptRef.current, BACKOFF.length - 1)];
        attemptRef.current++;
        reconnectRef.current = setTimeout(connect, delay);
      }
    }

    void connect();

    return () => {
      mountedRef.current = false;
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
      if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
      if (pongTimeoutRef.current) { clearTimeout(pongTimeoutRef.current); pongTimeoutRef.current = null; }
      try { wsRef.current?.close(1000, 'unmount'); } catch { /* noop */ }
      wsRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return status;
}
